<script setup lang="ts">
/**
 * Corps d'un article de blog + toute sa typographie.
 *
 * Le markup `.article-content` et les ~250 lignes de `:deep()` qui le stylent
 * vivaient dans `pages/blog/[slug].vue`, qui dépassait la limite ESLint de
 * 800 lignes. Les deux sont déplacés ENSEMBLE : les règles `:deep()` ne
 * s'appliquent qu'à l'élément porté par le template du composant, les séparer
 * aurait supprimé toute la mise en forme des articles.
 *
 * `html` doit arriver DÉJÀ sanitizé (DOMPurify) — le composant ne sanitize pas.
 */
defineProps<{ html: string }>();
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <!-- Reason: contenu sanitizé en amont par l'appelant (DOMPurify) -->
  <!-- Force prose-p:text-gray-700 pour contrer le default Tailwind
       Typography qui rend les <p> trop pâles (rgb(82 82 91)). -->
  <div
    class="article-content prose prose-lg dark:prose-invert max-w-none prose-p:text-gray-700 prose-li:text-gray-700 dark:prose-p:text-gray-300 dark:prose-li:text-gray-300"
    v-html="html"
  />
  <!-- eslint-enable vue/no-v-html -->
</template>

<style scoped>
/* Paragraphes — couleur forcée avec !important car prose-lg de
   Tailwind Typography surchargeait avec --tw-prose-body (trop pâle).
   Karim 2026-06-15. :not([style]) exclut les <p>/<li>/<strong> des CTA
   Donoor écrits en HTML inline dans Directus (fond bleu + texte blanc) :
   sans ça le !important écrasait leur style inline et les rendait
   illisibles. Karim 2026-08-10. */
.article-content :deep(p:not([style])) {
  font-size: 17px;
  line-height: 1.85;
  margin-bottom: 1.75rem;
  color: rgb(31 41 55) !important;
}

.article-content :deep(li:not([style])) {
  color: rgb(31 41 55) !important;
}

.article-content :deep(strong:not([style])) {
  color: rgb(17 24 39) !important;
}

@media (min-width: 640px) {
  .article-content :deep(p:not([style])) {
    font-size: 18px;
  }
}

.dark .article-content :deep(p:not([style])) {
  color: rgb(156 163 175);
}

/* Premier paragraphe mis en avant */
.article-content :deep(p:first-of-type) {
  font-size: 18px;
  line-height: 1.7;
  color: rgb(55 65 81);
  margin-bottom: 2rem;
}

@media (min-width: 640px) {
  .article-content :deep(p:first-of-type) {
    font-size: 20px;
  }
}

.dark .article-content :deep(p:first-of-type) {
  color: rgb(209 213 219);
}

/* Titres H2 - Sections principales.
   !important nécessaire : prose-lg de Tailwind Typography surcharge
   sinon avec --tw-prose-headings, et si l'utilisateur a `dark:prose-invert`
   actif (préférence système ou résidu localStorage), les H2 deviennent
   text-gray-200 sur fond blanc → illisibles. */
.article-content :deep(h2) {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgb(17 24 39) !important;
  margin-top: 3.5rem;
  margin-bottom: 1.5rem;
  line-height: 1.25;
  letter-spacing: -0.025em;
  padding-left: 1.25rem;
  border-left: 4px solid var(--color-main);
  scroll-margin-top: 6rem;
}

@media (min-width: 640px) {
  .article-content :deep(h2) {
    font-size: 1.875rem;
  }
}

@media (min-width: 1024px) {
  .article-content :deep(h2) {
    font-size: 2rem;
  }
}

/* Pas d'override .dark : on garde le H2 sombre quoi qu'il arrive
   (le composant n'a pas de vrai dark mode, et le résidu colormode
   posait des H2 illisibles sur fond blanc). */

/* Titres H3 - Sous-sections (même raison que H2 : !important pour
   bloquer prose-invert qui rend les H3 quasi-invisibles sur fond blanc). */
.article-content :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  color: rgb(31 41 55) !important;
  margin-top: 2.5rem;
  margin-bottom: 1rem;
  line-height: 1.4;
  scroll-margin-top: 6rem;
}

@media (min-width: 640px) {
  .article-content :deep(h3) {
    font-size: 1.5rem;
  }
}

/* Pas d'override .dark sur H3 — voir commentaire H2 ci-dessus. */

/* Texte en gras */
.article-content :deep(strong) {
  font-weight: 600;
  color: rgb(17 24 39);
}

.dark .article-content :deep(strong) {
  color: rgb(255 255 255);
}

/* Listes non ordonnées */
.article-content :deep(ul) {
  margin-top: 1.75rem;
  margin-bottom: 1.75rem;
  padding-left: 0;
  list-style: none;
}

.article-content :deep(ul li) {
  font-size: 17px;
  line-height: 1.7;
  color: rgb(75 85 99);
  padding-left: 1.75rem;
  position: relative;
  margin-bottom: 0.75rem;
}

@media (min-width: 640px) {
  .article-content :deep(ul li) {
    font-size: 18px;
  }
}

.dark .article-content :deep(ul li) {
  color: rgb(156 163 175);
}

.article-content :deep(ul li)::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.6em;
  width: 8px;
  height: 8px;
  background-color: var(--color-main);
  border-radius: 50%;
}

/* Listes ordonnées */
.article-content :deep(ol) {
  margin-top: 1.75rem;
  margin-bottom: 1.75rem;
  padding-left: 1.5rem;
  list-style: decimal;
}

.article-content :deep(ol li) {
  font-size: 17px;
  line-height: 1.7;
  color: rgb(75 85 99);
  padding-left: 0.5rem;
  margin-bottom: 0.75rem;
}

@media (min-width: 640px) {
  .article-content :deep(ol li) {
    font-size: 18px;
  }
}

.dark .article-content :deep(ol li) {
  color: rgb(156 163 175);
}

.article-content :deep(ol li)::marker {
  color: var(--color-main);
  font-weight: 600;
}

/* Séparateurs */
.article-content :deep(hr) {
  margin-top: 3rem;
  margin-bottom: 3rem;
  border: 0;
  height: 1px;
  background-color: rgb(229 231 235);
}

.dark .article-content :deep(hr) {
  background-color: rgb(31 41 55);
}

/* Liens */
.article-content :deep(a) {
  color: var(--color-main);
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 4px;
  transition: color 0.2s;
}

.article-content :deep(a:hover) {
  opacity: 0.8;
}

/* Citations */
.article-content :deep(blockquote) {
  margin-top: 2rem;
  margin-bottom: 2rem;
  padding: 1.25rem 1.5rem;
  border-left: 4px solid var(--color-main);
  background-color: rgb(249 250 251);
  border-radius: 0 0.75rem 0.75rem 0;
  font-style: normal;
}

.dark .article-content :deep(blockquote) {
  background-color: rgba(0, 0, 0, 0.2);
}

.article-content :deep(blockquote p) {
  margin-bottom: 0;
  color: rgb(55 65 81);
}

.dark .article-content :deep(blockquote p) {
  color: rgb(209 213 219);
}

/* Code inline */
.article-content :deep(code) {
  color: var(--color-main);
  background-color: rgb(243 244 246);
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 15px;
  font-weight: 500;
}

.dark .article-content :deep(code) {
  background-color: rgb(31 41 55);
}

/* Images */
.article-content :deep(img) {
  border-radius: 0.75rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
  margin-bottom: 2rem;
}

/* Tableaux comparatifs Directus — display:block permet à overflow-x de
   fonctionner sans wrapper <div> supplémentaire à injecter dans le HTML
   brut. Sans ça, un tableau large (ex: comparatif 6 colonnes) pousse tout
   l'article hors du viewport mobile (overflow-x: clip global le tronque
   silencieusement, sans barre de scroll). */
.article-content :deep(table) {
  display: block;
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

.article-content :deep(pre) {
  overflow-x: auto;
  max-width: 100%;
}
</style>
